import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  ImageOff,
  Loader,
  X,
  Clock,
  ChefHat,
  MapPin,
  Youtube,
  UtensilsCrossed,
  ListOrdered,
} from "lucide-react";

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const UNSPLASH_ACCESS_KEY = "z9t1LOdrMzSMN2N-ctpFnDrTNUvDACdPLsS4lwUEWRk";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://www.bharatabroad.com/api";

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(to bottom right, #fff7ed, #fef3c7)",
    padding: "24px",
  },
  wrapper: { maxWidth: "1280px", margin: "0 auto" },
  header: { textAlign: "center", marginBottom: "32px" },
  title: {
    fontSize: "2.25rem",
    fontWeight: "bold",
    color: "#9a3412",
    marginBottom: "8px",
  },
  subtitle: { color: "#ea580c" },
  card: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    padding: "24px",
    marginBottom: "24px",
  },
  searchContainer: { position: "relative", marginBottom: "24px" },
  searchInput: {
    width: "100%",
    paddingLeft: "40px",
    paddingRight: "16px",
    paddingTop: "12px",
    paddingBottom: "12px",
    border: "2px solid #fed7aa",
    borderRadius: "8px",
    outline: "none",
    fontSize: "16px",
    boxSizing: "border-box",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "12px",
    color: "#9ca3af",
  },
  buttonGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "24px",
  },
  button: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    fontWeight: "600",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  buttonActive: {
    backgroundColor: "#f97316",
    color: "white",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    transform: "scale(1.1)",
  },
  buttonAvailable: { backgroundColor: "#ffedd5", color: "#9a3412" },
  buttonDisabled: {
    backgroundColor: "#f3f4f6",
    color: "#9ca3af",
    cursor: "not-allowed",
  },
  foodGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px",
  },
  foodItem: {
    background: "linear-gradient(to right, #fff7ed, #fef3c7)",
    borderRadius: "8px",
    border: "1px solid #fed7aa",
    transition: "all 0.2s",
    overflow: "hidden",
    cursor: "pointer",
  },
  imageContainer: {
    width: "100%",
    height: "200px",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
  },
  foodImage: { width: "100%", height: "100%", objectFit: "cover" },
  imagePlaceholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    color: "#9ca3af",
  },
  foodContent: { padding: "16px" },
  foodText: {
    color: "#7c2d12",
    fontWeight: "600",
    margin: 0,
    fontSize: "1rem",
  },
  foodRegion: { color: "#c2410c", fontSize: "0.8rem", margin: "4px 0 0 0" },
  emptyState: { textAlign: "center", padding: "48px 0", color: "#6b7280" },
  loader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    color: "#f97316",
  },
  dataLoader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "64px 0",
    gap: "12px",
    color: "#ea580c",
  },
  errorState: { textAlign: "center", padding: "48px 0", color: "#dc2626" },
};

/* ─── FoodImage — Wikimedia primary, Unsplash fallback ───────────────────── */
function FoodImage({ food, style }) {
  // stage: "primary" → "fetching" → "fallback" → "failed"
  const [stage, setStage] = useState("primary");
  const [fallbackUrl, setFallbackUrl] = useState(null);

  const fetchUnsplash = async () => {
    setStage("fetching");
    try {
      const res = await fetch(
        `https://api.unsplash.com/search/photos?query=indian+food+${encodeURIComponent(
          food.name
        )}&per_page=1&client_id=${UNSPLASH_ACCESS_KEY}`
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      const url = data.results?.[0]?.urls?.small;
      if (url) {
        setFallbackUrl(url);
        setStage("fallback");
      } else {
        setStage("failed");
      }
    } catch {
      setStage("failed");
    }
  };

  if (stage === "fetching") {
    return (
      <div style={styles.loader}>
        <Loader size={28} style={{ animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (stage === "failed") {
    return (
      <div style={styles.imagePlaceholder}>
        <ImageOff size={48} />
        <span style={{ fontSize: "0.875rem" }}>Image not available</span>
      </div>
    );
  }

  const src = stage === "fallback" ? fallbackUrl : food.image;

  return (
    <img
      src={src}
      alt={food.name}
      style={style || styles.foodImage}
      loading="lazy"
      onError={() => {
        if (stage === "primary") fetchUnsplash();
        else setStage("failed");
      }}
    />
  );
}

/* ─── Detail Modal ───────────────────────────────────────────────────────── */
function FoodDetailModal({ food, onClose }) {
  const difficultyColor =
    food.difficulty === "Easy"
      ? "#16a34a"
      : food.difficulty === "Hard"
      ? "#dc2626"
      : "#d97706";

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        animation: "fadeIn 0.2s ease",
      }}
    >
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(32px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes spin    { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        .modal-scroll::-webkit-scrollbar       { width: 6px; }
        .modal-scroll::-webkit-scrollbar-track { background: #fef3c7; }
        .modal-scroll::-webkit-scrollbar-thumb { background: #f97316; border-radius: 3px; }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "760px",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
          animation: "slideUp 0.25s ease",
        }}
      >
        {/* Hero image */}
        <div
          style={{
            position: "relative",
            height: "260px",
            flexShrink: 0,
            backgroundColor: "#f3f4f6",
          }}
        >
          <FoodImage
            food={food}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 55%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "16px",
              left: "20px",
              right: "56px",
            }}
          >
            <h2
              style={{
                color: "white",
                margin: 0,
                fontSize: "1.75rem",
                fontWeight: "800",
                textShadow: "0 2px 8px rgba(0,0,0,0.4)",
              }}
            >
              {food.name}
            </h2>
            {food.region && (
              <span
                style={{
                  color: "#fed7aa",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "4px",
                }}
              >
                <MapPin size={13} /> {food.region}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(0,0,0,0.5)",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.85)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.5)")
            }
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div
          className="modal-scroll"
          style={{
            overflowY: "auto",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {/* Badges */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {food.prepTime && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: "20px",
                  padding: "6px 14px",
                  fontSize: "0.85rem",
                  color: "#9a3412",
                }}
              >
                <Clock size={14} color="#f97316" /> {food.prepTime}
              </span>
            )}
            {food.difficulty && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: "20px",
                  padding: "6px 14px",
                  fontSize: "0.85rem",
                  color: difficultyColor,
                }}
              >
                <ChefHat size={14} color={difficultyColor} /> {food.difficulty}
              </span>
            )}
          </div>

          {/* Description */}
          {food.description && (
            <p
              style={{
                margin: 0,
                color: "#44403c",
                lineHeight: "1.7",
                fontSize: "0.95rem",
                borderLeft: "3px solid #f97316",
                paddingLeft: "14px",
              }}
            >
              {food.description}
            </p>
          )}

          {/* Ingredients + Steps */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "20px",
            }}
          >
            {food.ingredients?.length > 0 && (
              <div>
                <h3
                  style={{
                    margin: "0 0 12px 0",
                    color: "#9a3412",
                    fontSize: "0.95rem",
                    fontWeight: "700",
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                  }}
                >
                  <UtensilsCrossed size={16} color="#f97316" /> Ingredients
                </h3>
                <ul
                  style={{
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: "7px",
                  }}
                >
                  {food.ingredients.map((ing, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                        fontSize: "0.875rem",
                        color: "#57534e",
                      }}
                    >
                      <span
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: "#f97316",
                          marginTop: "6px",
                          flexShrink: 0,
                        }}
                      />
                      {ing}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {food.preparation?.length > 0 && (
              <div>
                <h3
                  style={{
                    margin: "0 0 12px 0",
                    color: "#9a3412",
                    fontSize: "0.95rem",
                    fontWeight: "700",
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                  }}
                >
                  <ListOrdered size={16} color="#f97316" /> Preparation
                </h3>
                <ol
                  style={{
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {food.preparation.map((step, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        fontSize: "0.875rem",
                        color: "#57534e",
                        lineHeight: "1.55",
                      }}
                    >
                      <span
                        style={{
                          minWidth: "24px",
                          height: "24px",
                          background: "#f97316",
                          color: "white",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.72rem",
                          fontWeight: "700",
                          flexShrink: 0,
                          marginTop: "1px",
                        }}
                      >
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* YouTube */}
          {food.youtube && (
            <a
              href={food.youtube}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                background: "#ff0000",
                color: "white",
                borderRadius: "8px",
                padding: "12px 20px",
                textDecoration: "none",
                fontWeight: "600",
                fontSize: "0.9rem",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <Youtube size={20} /> Watch Recipe on YouTube
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function IndianFoodFilter() {
  const [foodData, setFoodData] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState("A");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedFood, setSelectedFood] = useState(null);
  const debounceRef = useRef(null);

  // ── Fetch food data from backend ────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setDataLoading(true);
        setDataError(null);
        const res = await fetch(`${API_BASE_URL}/indian-foods`);
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const json = await res.json();
        const transformed = {};
        (json.sections || []).forEach((section) => {
          if (section.letter && Array.isArray(section.foods))
            transformed[section.letter] = section.foods;
        });
        setFoodData(transformed);
      } catch (err) {
        setDataError(err.message);
      } finally {
        setDataLoading(false);
      }
    })();
  }, []);

  // ── Debounce search ──────────────────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  const filteredFoods = debouncedSearch
    ? Object.values(foodData)
        .flat()
        .filter((f) =>
          f.name.toLowerCase().includes(debouncedSearch.toLowerCase())
        )
    : foodData[selectedLetter] || [];

  const availableLetters = Object.keys(foodData);

  const getButtonStyle = (letter) => {
    const base = { ...styles.button };
    if (selectedLetter === letter && !debouncedSearch)
      return { ...base, ...styles.buttonActive };
    if (availableLetters.includes(letter))
      return { ...base, ...styles.buttonAvailable };
    return { ...base, ...styles.buttonDisabled };
  };

  const handleLetterClick = (letter) => {
    setSelectedLetter(letter);
    setSearchTerm("");
    setDebouncedSearch("");
  };

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>

      <div style={styles.container}>
        <div style={styles.wrapper}>
          <div style={styles.header}>
            <h1 style={styles.title}>Authentic Bharat Cuisines</h1>
            <p style={styles.subtitle}>
              Explore delicious Indian dishes alphabetically
            </p>
          </div>

          <div style={styles.card}>
            <div style={styles.searchContainer}>
              <Search style={styles.searchIcon} size={20} />
              <input
                type="text"
                placeholder="Search for a dish (e.g., Biryani, Dosa, Paneer)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
                onFocus={(e) => (e.target.style.borderColor = "#fb923c")}
                onBlur={(e) => (e.target.style.borderColor = "#fed7aa")}
              />
            </div>

            <div style={styles.buttonGrid}>
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  onClick={() => handleLetterClick(letter)}
                  disabled={!availableLetters.includes(letter)}
                  style={getButtonStyle(letter)}
                  onMouseEnter={(e) => {
                    if (availableLetters.includes(letter))
                      e.target.style.backgroundColor = "#fdba74";
                  }}
                  onMouseLeave={(e) => {
                    if (selectedLetter === letter && !debouncedSearch)
                      e.target.style.backgroundColor = "#f97316";
                    else if (availableLetters.includes(letter))
                      e.target.style.backgroundColor = "#ffedd5";
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>

            {debouncedSearch && (
              <div
                style={{
                  marginTop: "16px",
                  color: "#ea580c",
                  fontSize: "14px",
                }}
              >
                Showing results for: <strong>{debouncedSearch}</strong>
                {filteredFoods.length === 0 && " — No dishes found"}
              </div>
            )}
          </div>

          <div style={styles.card}>
            {dataLoading && (
              <div style={styles.dataLoader}>
                <Loader
                  size={40}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                <p style={{ margin: 0, fontWeight: "500" }}>
                  Loading dishes from server…
                </p>
              </div>
            )}

            {!dataLoading && dataError && (
              <div style={styles.errorState}>
                <p style={{ fontSize: "1.125rem", fontWeight: "600" }}>
                  Failed to load dishes
                </p>
                <p
                  style={{
                    fontSize: "0.875rem",
                    marginTop: "8px",
                    color: "#6b7280",
                  }}
                >
                  {dataError}
                </p>
                <p
                  style={{
                    fontSize: "0.8rem",
                    marginTop: "8px",
                    color: "#9ca3af",
                  }}
                >
                  Endpoint: <code>{API_BASE_URL}/indian-foods</code>
                </p>
              </div>
            )}

            {!dataLoading &&
              !dataError &&
              (filteredFoods.length > 0 ? (
                <div style={styles.foodGrid}>
                  {filteredFoods.map((food, index) => (
                    <div
                      key={`${food.name}-${index}`}
                      style={styles.foodItem}
                      onClick={() => setSelectedFood(food)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 4px 8px rgba(0,0,0,0.15)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <div style={styles.imageContainer}>
                        <FoodImage food={food} />
                      </div>
                      <div style={styles.foodContent}>
                        <p style={styles.foodText}>{food.name}</p>
                        {food.region && (
                          <p style={styles.foodRegion}>📍 {food.region}</p>
                        )}
                        {(food.prepTime || food.difficulty) && (
                          <p
                            style={{
                              color: "#78716c",
                              fontSize: "0.78rem",
                              margin: "4px 0 0 0",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {food.prepTime && (
                              <>
                                <Clock size={11} /> {food.prepTime}
                              </>
                            )}
                            {food.difficulty && (
                              <span
                                style={{
                                  marginLeft: food.prepTime ? "8px" : 0,
                                  color:
                                    food.difficulty === "Easy"
                                      ? "#16a34a"
                                      : food.difficulty === "Hard"
                                      ? "#dc2626"
                                      : "#d97706",
                                }}
                              >
                                {food.prepTime ? "· " : ""}
                                {food.difficulty}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  <p style={{ fontSize: "1.125rem" }}>No dishes found</p>
                  {debouncedSearch && (
                    <p style={{ fontSize: "0.875rem", marginTop: "8px" }}>
                      Try searching for something else like "Biryani", "Dosa",
                      or "Paneer"
                    </p>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>

      {selectedFood && (
        <FoodDetailModal
          food={selectedFood}
          onClose={() => setSelectedFood(null)}
        />
      )}
    </>
  );
}
